import { useState, useEffect } from 'react';
import { api } from '../api/client';

export interface DailyLog {
  id?: string;
  date: string;
  accomplishments: string;
  learnings: string;
  challenges: string;
  tomorrow_goals: string;
  mood: number; // 1 to 5
  ai_insight?: string;
  created_at?: string;
}

export function GrowthPage() {
  const todayStr = new Date().toISOString().split('T')[0]!;
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [sendingVoiceAlert, setSendingVoiceAlert] = useState(false);

  const handleSendVoiceAlert = async () => {
    setSendingVoiceAlert(true);
    try {
      await api.sendProactiveVoiceAlert({
        message: 'Chào bạn, hôm nay bạn đã hoàn thành những mục tiêu gì? Hãy dành 2 phút cập nhật Daily Growth Log để lưu giữ hành trình tri thức nhé.',
        type: 'growth_reminder'
      });
      alert('🎙️ Đã phát thông báo nhắc nhở bằng giọng nói qua ErixVoice MCP thành công!');
    } catch (e: any) {
      alert('Lỗi gửi cảnh báo giọng nói: ' + e.message);
    } finally {
      setSendingVoiceAlert(false);
    }
  };

  const [currentLog, setCurrentLog] = useState<DailyLog>({
    date: todayStr,
    accomplishments: '',
    learnings: '',
    challenges: '',
    tomorrow_goals: '',
    mood: 4,
    ai_insight: '',
  });

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await api.getGrowthLogs();
      const loadedLogs: DailyLog[] = Array.isArray(data.logs) ? data.logs : [];
      setLogs(loadedLogs);
      
      const found = loadedLogs.find(l => l.date === selectedDate);
      if (found) {
        setCurrentLog(found);
      } else {
        setCurrentLog({
          date: selectedDate,
          accomplishments: '',
          learnings: '',
          challenges: '',
          tomorrow_goals: '',
          mood: 4,
          ai_insight: '',
        });
      }
    } catch (e) {
      console.error('Failed to load growth logs', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    const found = logs.find(l => l.date === date);
    if (found) {
      setCurrentLog(found);
    } else {
      setCurrentLog({
        date,
        accomplishments: '',
        learnings: '',
        challenges: '',
        tomorrow_goals: '',
        mood: 4,
        ai_insight: '',
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.saveGrowthLog(currentLog);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      
      // Update local logs list
      setLogs(prev => {
        const idx = prev.findIndex(l => l.date === currentLog.date);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...currentLog };
          return updated;
        }
        return [currentLog, ...prev];
      });
    } catch (e: any) {
      alert('Lưu thất bại: ' + (e.message || 'Lỗi không xác định'));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAiInsight = async () => {
    if (!currentLog.accomplishments && !currentLog.learnings && !currentLog.challenges) {
      alert('Vui lòng nhập ít nhất thành tựu, bài học hoặc thách thức trước khi yêu cầu AI phân tích.');
      return;
    }
    setAnalyzing(true);
    try {
      const prompt = `Bạn là Chuyên gia Cố vấn Phát triển Cá nhân (Executive & Cognitive Coach). 
Hãy phân tích nhật ký ngày ${currentLog.date} sau:
- Thành tựu: ${currentLog.accomplishments || 'Chưa ghi'}
- Bài học: ${currentLog.learnings || 'Chưa ghi'}
- Thách thức: ${currentLog.challenges || 'Chưa ghi'}
- Mục tiêu ngày mai: ${currentLog.tomorrow_goals || 'Chưa ghi'}
- Chỉ số năng lượng/tâm trạng: ${currentLog.mood}/5

Hãy đưa ra phản tư (Cognitive Reflection) cô đọng gồm 3 phần:
1. 🌟 Nhận định điểm sáng (Highlight)
2. 💡 Quy luật tư duy & góc nhìn mới (Mental Model / Mindset shift)
3. 🎯 Lời khuyên tối ưu cho ngày mai (Actionable advice)`;

      const res = await api.query(prompt);
      const insightText = res.context || 'Đã phân tích nhật ký.';
      const updated = { ...currentLog, ai_insight: insightText };
      setCurrentLog(updated);
      await api.saveGrowthLog(updated);
      
      setLogs(prev => {
        const idx = prev.findIndex(l => l.date === updated.date);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updated;
          return next;
        }
        return [updated, ...prev];
      });
    } catch (e: any) {
      alert('Lỗi khi sinh AI Insight: ' + (e.message || 'Unknown error'));
    } finally {
      setAnalyzing(false);
    }
  };

  const MOOD_EMOJIS = ['😫 Kiệt sức', '😕 Mệt mỏi', '😐 Bình thường', '🙂 Tích cực', '🔥 Thăng hoa'];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', flexDirection: 'column' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang tải Daily Growth Log...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>
      
      {/* Page Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">📈 Daily Growth Log &amp; AI Insights</h1>
          <p className="page-subtitle">
            Hệ thống lưu trữ ký ức (.ai/memory/) ghi lại tiến trình tăng trưởng, phản tư và đúc kết quy luật tư duy cá nhân
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn--secondary"
            onClick={handleSendVoiceAlert}
            disabled={sendingVoiceAlert}
            title="Gửi cảnh báo giọng nói chủ động qua ErixVoice MCP"
            style={{ color: '#38bdf8', borderColor: 'rgba(56,189,248,0.4)', background: 'rgba(56,189,248,0.1)' }}
          >
            {sendingVoiceAlert ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Đang gửi...</> : '🎙️ Voice Alert'}
          </button>
          <button
            className="btn btn--secondary"
            onClick={handleGenerateAiInsight}
            disabled={analyzing}
            style={{ color: '#c084fc', borderColor: 'rgba(124,58,237,0.4)', background: 'rgba(124,58,237,0.1)' }}
          >
            {analyzing ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Đang suy ngẫm...</> : '✨ AI Phân Tích'}
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving}
            style={{ minWidth: 100 }}
          >
            {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : saved ? '✅ Đã lưu!' : '💾 Lưu Nhật Ký'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, gap: '20px', overflow: 'hidden' }}>

        {/* Left Sidebar: Timeline History */}
        <div style={{
          width: 240,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          background: 'var(--bg-surface)',
          borderRadius: '12px',
          border: '1px solid var(--border)',
          padding: '14px',
          overflowY: 'auto'
        }}>
          <div style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
            📅 Lịch Sử Nhật Ký
          </div>
          
          <input
            type="date"
            className="input"
            value={selectedDate}
            onChange={e => e.target.value && handleDateChange(e.target.value)}
            style={{ fontSize: '13px', padding: '6px 10px' }}
          />

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px' }}>
            {logs.length === 0 ? (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '8px 0' }}>
                Chưa có nhật ký nào trước đây.
              </div>
            ) : (
              logs.map(log => {
                const isSelected = log.date === selectedDate;
                return (
                  <button
                    key={log.date}
                    onClick={() => handleDateChange(log.date)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 10px',
                      borderRadius: '8px',
                      border: 'none',
                      background: isSelected ? 'rgba(6,182,212,0.15)' : 'transparent',
                      color: isSelected ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontSize: '12.5px',
                      fontWeight: isSelected ? 600 : 400,
                      textAlign: 'left',
                      transition: 'all 0.15s'
                    }}
                  >
                    <span>{log.date === todayStr ? `🌟 Hôm nay` : log.date}</span>
                    <span style={{ fontSize: '10px', opacity: 0.8 }}>
                      {log.mood === 5 ? '🔥' : log.mood === 4 ? '🙂' : log.mood === 3 ? '😐' : log.mood === 2 ? '😕' : '😫'}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Center: Growth Log Form */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto', paddingRight: '4px' }}>
          
          {/* Mood Selector */}
          <div className="card" style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
              ⚡ Năng lượng &amp; Cảm hứng ngày {selectedDate}:
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[1, 2, 3, 4, 5].map(m => (
                <button
                  key={m}
                  onClick={() => setCurrentLog(prev => ({ ...prev, mood: m }))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: `1px solid ${currentLog.mood === m ? 'var(--accent-cyan)' : 'var(--border)'}`,
                    background: currentLog.mood === m ? 'rgba(6,182,212,0.18)' : 'var(--bg-glass)',
                    color: currentLog.mood === m ? 'var(--accent-cyan)' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: currentLog.mood === m ? 600 : 400,
                    transition: 'all 0.15s'
                  }}
                >
                  {MOOD_EMOJIS[m - 1]}
                </button>
              ))}
            </div>
          </div>

          {/* Form grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            
            {/* Box 1: Accomplishments */}
            <div className="card">
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#34d399' }}>
                <span>🌟</span> Thành tựu chính hôm nay
              </div>
              <textarea
                className="textarea"
                value={currentLog.accomplishments}
                onChange={e => setCurrentLog(prev => ({ ...prev, accomplishments: e.target.value }))}
                placeholder="Những việc quan trọng bạn đã giải quyết xong? Cột mốc nhỏ đạt được?"
                style={{ height: '110px', fontSize: '13px', lineHeight: 1.6 }}
              />
            </div>

            {/* Box 2: Learnings */}
            <div className="card">
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#38bdf8' }}>
                <span>💡</span> Bài học &amp; Điều tâm đắc
              </div>
              <textarea
                className="textarea"
                value={currentLog.learnings}
                onChange={e => setCurrentLog(prev => ({ ...prev, learnings: e.target.value }))}
                placeholder="Một kinh nghiệm mới, một góc nhìn thú vị hoặc sai lầm đã rút ra bài học?"
                style={{ height: '110px', fontSize: '13px', lineHeight: 1.6 }}
              />
            </div>

            {/* Box 3: Challenges */}
            <div className="card">
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#f87171' }}>
                <span>🚧</span> Thách thức &amp; Rào cản
              </div>
              <textarea
                className="textarea"
                value={currentLog.challenges}
                onChange={e => setCurrentLog(prev => ({ ...prev, challenges: e.target.value }))}
                placeholder="Điều gì làm bạn mất tập trung hoặc đang gây ách tắc công việc?"
                style={{ height: '110px', fontSize: '13px', lineHeight: 1.6 }}
              />
            </div>

            {/* Box 4: Tomorrow Goals */}
            <div className="card">
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: '#c084fc' }}>
                <span>🎯</span> Trọng tâm ngày mai
              </div>
              <textarea
                className="textarea"
                value={currentLog.tomorrow_goals}
                onChange={e => setCurrentLog(prev => ({ ...prev, tomorrow_goals: e.target.value }))}
                placeholder="Top 1-3 việc quan trọng nhất bạn cam kết hoàn thành vào ngày mai?"
                style={{ height: '110px', fontSize: '13px', lineHeight: 1.6 }}
              />
            </div>

          </div>

          {/* AI Cognitive Reflection Box */}
          {currentLog.ai_insight && (
            <div className="card" style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(6,182,212,0.06) 100%)', border: '1px solid rgba(124,58,237,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 700, color: '#c084fc' }}>
                  <span>✨</span> AI Cognitive Reflection — Cố Vấn Tăng Trưởng
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Cập nhật tự động</span>
              </div>
              <div style={{ fontSize: '13.5px', lineHeight: 1.7, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {currentLog.ai_insight}
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
}
